/**
 * Marketing-site price strings, all derived from `@shared/pricing` so no
 * public page ever hard-codes a number. If the pricing module changes, every
 * card, estimator line, FAQ answer and footnote follows.
 */
import {
  PRICING,
  hostMonthlyCents,
  ownerPortalMonthlyCents,
} from "@shared/pricing";

/** "$49", "$1,075", "$12.50" — whole dollars drop the cents. */
export function formatUsd(cents: number): string {
  const dollars = cents / 100;
  return dollars.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: Number.isInteger(dollars) ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

/** 1 → "1st", 11 → "11th", 51 → "51st". */
export function ordinal(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

const tiers = PRICING.hostTiersMonthly;
const [baseTier, ...stepTiers] = tiers;

/** "$49" — the price of a host's first properties. */
export const HOST_BASE_PRICE = formatUsd(baseTier.unitAmount);
/** Number of properties billed at the base price (10 under D2). */
export const HOST_BASE_TIER_SIZE = baseTier.toProperty ?? Infinity;
/** "$10" */
export const OWNER_PRICE = formatUsd(PRICING.ownerPortal.monthlyPerOwner);
/** 14 */
export const TRIAL_DAYS = PRICING.trial.days;
/** 30 */
export const PAUSED_RETENTION_DAYS = PRICING.trial.pausedDataRetentionDays;
/** 5 */
export const FREE_RESEARCH_SEARCHES = PRICING.research.freeSearchesPerMonth;
/** 30 */
export const MONEY_BACK_DAYS = PRICING.refunds.annualDays;

/** The volume steps after the base tier: [{ price: "$39", from: "11th", fromProperty: 11, toProperty: 50 }, …]. */
export const HOST_TIER_STEPS = stepTiers.map((t) => ({
  price: formatUsd(t.unitAmount),
  from: ordinal(t.fromProperty),
  fromProperty: t.fromProperty,
  toProperty: t.toProperty,
}));

/** "$39 a property from your 11th · $29 from your 51st" (A3 Host card). */
export const TIER_STEPS_LINE = HOST_TIER_STEPS.map((s, i) =>
  i === 0 ? `${s.price} a property from your ${s.from}` : `${s.price} from your ${s.from}`,
).join(" · ");

/** "$39 from your 11th, $29 from your 51st" (A3 PM card, A5, A8, A9). */
export const TIER_STEPS_SHORT = HOST_TIER_STEPS.map((s) => `${s.price} from your ${s.from}`).join(", ");

/** "$49 a property · 14-day free trial, no card · Cancel anytime" (A2). */
export const TRUST_LINE = `${HOST_BASE_PRICE} a property · ${TRIAL_DAYS}-day free trial, no card · Cancel anytime`;

/** Pricing FAQ answer for "How does the price change as I grow?" (A3, D2 boundaries). */
export function growthFaqAnswer(exampleProperties = 12): string {
  const parts: string[] = [`Your first ${baseTier.toProperty} properties are ${HOST_BASE_PRICE} each`];
  HOST_TIER_STEPS.forEach((s) => {
    if (s.toProperty === null) {
      parts.push(`and every property from your ${s.from} is ${s.price}`);
    } else {
      parts.push(`properties ${s.fromProperty} to ${s.toProperty} are ${s.price} each`);
    }
  });
  const example = estimateBreakdown(exampleProperties, 0);
  return `${parts.join(", ")}. For example, ${exampleProperties} properties cost ${example.expression} = ${formatUsd(example.totalCents)} a month.`;
}

export interface EstimateBreakdown {
  totalCents: number;
  /** "10 × $49 + 2 × $39" (+ " + 3 owners × $10" when owners > 0). Empty when there is nothing to bill. */
  expression: string;
  /** "10 × $49 + 2 × $39 = $568/mo" or "$0/mo". */
  line: string;
}

/** Estimator math: `hostMonthlyCents(properties) + ownerPortalMonthlyCents(owners)` with a readable breakdown. */
export function estimateBreakdown(properties: number, owners: number): EstimateBreakdown {
  const p = Math.max(0, Math.floor(properties || 0));
  const o = Math.max(0, Math.floor(owners || 0));
  const terms: string[] = [];
  for (const t of tiers) {
    const upper = t.toProperty === null ? p : Math.min(p, t.toProperty);
    const count = upper - t.fromProperty + 1;
    if (count > 0) terms.push(`${count} × ${formatUsd(t.unitAmount)}`);
  }
  if (o > 0) terms.push(`${o} ${o === 1 ? "owner" : "owners"} × ${OWNER_PRICE}`);
  const totalCents = hostMonthlyCents(p) + ownerPortalMonthlyCents(o);
  const expression = terms.join(" + ");
  const total = `${formatUsd(totalCents)}/mo`;
  return {
    totalCents,
    expression,
    line: terms.length > 0 ? `${expression} = ${total}` : total,
  };
}

/** A3 example table rows, computed from the module. */
export const EXAMPLE_PROPERTY_COUNTS = [1, 2, 5, 10, 15, 25, 50, 100] as const;
export function exampleRows(): Array<{ properties: number; monthly: string }> {
  return EXAMPLE_PROPERTY_COUNTS.map((n) => ({ properties: n, monthly: formatUsd(hostMonthlyCents(n)) }));
}

/** Annual copy pieces — only rendered when `isAnnualEnabled()`. */
export const ANNUAL_MONTHS_CHARGED = PRICING.annual.monthsCharged;
export const ANNUAL_MONTHS_COVERED = PRICING.annual.monthsCovered;

/** Lifetime (SYRE) offer strings. */
export const LIFETIME_PRICE = formatUsd(PRICING.lifetime.priceOneTime);
export const LIFETIME_ALLOWANCE = PRICING.lifetime.propertyAllowance;
export const LIFETIME_REFUND_DAYS = PRICING.refunds.lifetimeDays;
